import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Container,
    FormControl,
    FormControlLabel,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Link
} from "@mui/material";
import {Close, ExpandMore, NavigateNext, TaskAlt, WarningAmber, Edit, ArrowBack, ContentCopy as IconCopy, ArrowRight} from "@mui/icons-material";
import {useNavigate, useSearchParams, useParams, useNavigation} from "react-router-dom";
import {urls} from "@routes";
import {Dispatch, useEffect, useReducer, useState, useRef} from "react";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn, validateDomainTLDAllowed} from "@lib/apis";
import { extractDomainTLDFromDomain, isValidDomainFormat } from "@lib/utils";
import {mirage} from "ldrs";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import CopyButton from "@components/CopyButton";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {useSnackbar} from "notistack";
import {format} from "date-fns";
import CountrySelect from "@components/CountrySelect";
import IconButton from "@mui/material/IconButton";

mirage.register();

interface PageData {
	status_code: number;
	status_text: string;

	setup_data?: {
		setup_complete: boolean
		current_stage: number
		subdomain: string
		nameservers: string[]
		naming_strategy: string
		custom_name: string | null
		contacts_count: number
	}
}

interface CustomNameRow {
    first: string;
    last: string;
}

// ------------------ WIZARD REDUCER ------------------

type State = {
	setupComplete: boolean
	stageNumber: number
	subdomain: string
	nameservers: string[],
	namingStrategy: string,
	customName: string[],
	contactsCount: number,
}

type LoadSavedStateAction = {
	type: "LOAD_SAVE_STATE",
	setupComplete: boolean,
	stageNumber: number,
	subdomain: string,
	nameservers: string[],
	namingStrategy: string,
	customName: string,
	contactsCount: number,
};
type AddManagedSubdomainAction = { type: "ADD_MANAGED_SUBDOMAIN", subdomain: string, nameservers: string[] };
type NameserversAddedAction = { type: "NAMESERVERS_ADDED" };
type NameserversVerifiedAction = { type: "NAMESERVERS_VERIFIED" };
type NamingStrategySetAction = {
	type: "NAMING_STRATEGY_SET",
	namingStrategy: string,
	customName: string[],
	contactsCount: number
};
type EmailGenerationPollingAction = { type: "EMAIL_GENERATION_POLLING" };
type EmailSetupPollingAction = { type: "EMAIL_SETUP_POLLING" };
type EmailAliasesCreated = { type: "EMAIL_ALIASES_CREATED" }

type Action = LoadSavedStateAction | AddManagedSubdomainAction | NameserversAddedAction | NameserversVerifiedAction |
	NamingStrategySetAction | EmailAliasesCreated | EmailGenerationPollingAction | EmailSetupPollingAction;

function reducer(wizardData: State, action: Action): State {
	switch (action.type) {
		case 'LOAD_SAVE_STATE':
			return {
				setupComplete: action.setupComplete,
				stageNumber: action.stageNumber,
				subdomain: action.subdomain,
				nameservers: action.nameservers,
				namingStrategy: action.namingStrategy,
				customName: Array.isArray(action.customName) ? action.customName : (action.customName ? action.customName.split(';') : []),
				contactsCount: action.contactsCount,
			}

		case 'ADD_MANAGED_SUBDOMAIN':
			return {
				...wizardData,
				stageNumber: 1,
				subdomain: action.subdomain,
				nameservers: action.nameservers
			}

		case 'NAMESERVERS_ADDED':
			return { ...wizardData, stageNumber: 2 };

		case 'NAMESERVERS_VERIFIED':
			return { ...wizardData, stageNumber: 3 };

		case 'NAMING_STRATEGY_SET':
			return {
				...wizardData,
				stageNumber: 4,
				namingStrategy: action.namingStrategy,
				contactsCount: action.contactsCount,
				customName: action.customName,
			};

		case 'EMAIL_GENERATION_POLLING':
			return {
				...wizardData,
				stageNumber: 5
			}

		case 'EMAIL_ALIASES_CREATED':
			return {
				...wizardData,
				stageNumber: 6,
			};

		case 'EMAIL_SETUP_POLLING':
			return {
				...wizardData,
				stageNumber: 7, // Setup complete page.
			};

		default:
			throw new Error("Invalid action type");
	}
}

// ------------------------------------------------------

export default function AdminConnectDomain() {
    const {userId, workspaceId} = useParams();
    const navigate = useNavigate();    
    const initalState: State = {
        setupComplete: false,
        stageNumber: 0,
        subdomain: "",
        nameservers: [],
        namingStrategy: "",
        customName: [],
        contactsCount: 0,
    }

    const [searchParams] = useSearchParams();
    const continueSubdomain: string | null = searchParams.get("subdomain");

    const [wizardData, dispatch] = useReducer(reducer, initalState);

    const [showLoader, setShowLoader] = useState(false);

    // --------------- Fetch the page data ---------------
    const pageDataQuery = useQuery({
        queryKey: ["connectExistingDomain"],
        queryFn: () => authenticateAndFetchData(continueSubdomain ?
            `/admin/connect-exising-domain/?subdomain=${continueSubdomain}&user_id=${userId}&workspace_id=${workspaceId}` :
            `/admin/connect-exising-domain/?user_id=${userId}&workspace_id=${workspaceId}`
        ),
        refetchOnWindowFocus: false,
        gcTime: 0,
        retry: retryFn,
    });

    useEffect(() => {
        if (pageDataQuery.data) {
            let data = pageDataQuery.data.data as PageData;
            if (data.setup_data) {
                dispatch({
                    type: "LOAD_SAVE_STATE",
                    setupComplete: data.setup_data.setup_complete,
                    stageNumber: data.setup_data.current_stage,
                    subdomain: data.setup_data.subdomain,
                    nameservers: data.setup_data.nameservers,
                    namingStrategy: data.setup_data.naming_strategy,
                    customName: data.setup_data.custom_name || "",
                    contactsCount: data.setup_data.contacts_count,
                });
            }
        }
    }, [pageDataQuery.data]);

    // --------------- WIZARD STAGES ---------------
    const stages = [
        <GetSubdomain userId={userId!} workspaceId={workspaceId!} dispatch={dispatch}
                                    setShowLoader={setShowLoader}/>,
        <AddNameservers userId={userId!} nameservers={wizardData.nameservers}
                                        subdomain={wizardData.subdomain}
                                        dispatch={dispatch}/>,
        <CheckNsRecords userId={userId!} workspaceId={workspaceId!} subdomain={wizardData.subdomain}
                                        dispatch={dispatch}/>,
        <EmailAndSubdomainGeneration userId={userId!} workspaceId={workspaceId!} subdomain={wizardData.subdomain}
                                                                 dispatch={dispatch}
                                                                 setShowLoader={setShowLoader}/>,
        <EmailGenerationPollingPage workspaceId={workspaceId!} subdomain={wizardData.subdomain} dispatch={dispatch}/>,
        <EmailList userId={userId!} workspaceId={workspaceId!} subdomain={wizardData.subdomain}
                             dispatch={dispatch}
                             setShowLoader={setShowLoader}/>,
        <EmailSetupPollingPage workspaceId={workspaceId!} subdomain={wizardData.subdomain} dispatch={dispatch}/>,
        <SetupComplete userId={userId!} workspaceId={workspaceId!} subdomain={wizardData.subdomain}/>,
    ]

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

function renderPage() {
        if (showLoader) {
            return <WizardProcessing />;
        } else {
            return stages[wizardData.stageNumber];
        }
    }

    const renderContent = () => {
        if (pageDataQuery.isLoading) {
            return <PageLoading />;
        }

        if (pageDataQuery.error as unknown as ApiRequestFailed) {
            if (pageDataQuery.error?.statusCode === 403) {
                return (
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        justifyContent="center" 
                        alignItems="center"
                        position="relative"
                        width="100%"
                        p={3}
                    >
                        <Box width="100%" position="relative" display="flex" justifyContent="center" alignItems="center">
                            <IconButton
                                onClick={() => navigate(urls["adminUserDetails"].replace(":userId", userId || ""))}
                                sx={{ position: "absolute", left: 0 }}
                            >
                                <ArrowBack />
                            </IconButton>

                            <Typography variant="h4" textAlign="center">
                                Max Domain Connection Limit Reached
                            </Typography>
                        </Box>
                        
                        <Typography variant="body1" textAlign="center" sx={{ mt: 2, mb: 4 }}>
                            You have reached your current plan's maximum allowed domain connections.<br />
                            Please upgrade to a higher plan for higher limits or delete any of your existing domains to make space.
                        </Typography>
                    </Box>
                );
            }
            return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />;
        }

        return (
            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
                {renderPage()}
            </Box>
        );
    };

    return (
        <>
            {/* Keeping the custom admin view scaffolding structure */}
            {renderContent()}
        </>
    );
}


function WizardProcessing() {
    return (
        <Stack direction={"column"} spacing={4} justifyContent={"center"} alignItems={"center"}>
            <Stack direction={"column"} spacing={1}>
                <Typography variant={"h4"} align={"center"}>Processing... Please Wait.</Typography>
                <Typography variant={"subtitle1"} align={"center"}>
                    Getting things ready for the next step. Please do not close this tab or refresh the page!
                </Typography>
            </Stack>
            <l-mirage size="120" color={"#42a5f5"}></l-mirage>
        </Stack>
    )
}

function CancelButton(props: {
    disable?: boolean,
    userId?: string
}) {
    const navigate = useNavigate();

    return (
        <Button variant={"outlined"} sx={{mr: 1}} startIcon={<Close/>} disabled={props.disable}
                        onClick={() => {
                            if (props.userId) {
                                navigate(urls["adminUserDetails"].replace(":userId", props.userId));
                            }
                        }}
                        >
            Cancel
        </Button>
    )
}

function GetSubdomain(props: {
    userId: string;
    workspaceId: string;
    dispatch: Dispatch<Action>;
    setShowLoader: (value: boolean) => void;
}) {
    const { enqueueSnackbar } = useSnackbar();

    const [domain, setDomain] = useState<string>("");
    const [domainError, setDomainError] = useState<string>("");
    const [isValidatingTLD, setIsValidatingTLD] = useState<boolean>(false);

    // --------------- Mutation for adding managed subdomain ---------------
    const addManagedSubdomainMutation = useMutation({
        mutationKey: ["addManagedSubdomain"],
        mutationFn: (fqdn: string) => 
            authenticateAndPostData("/admin/connect/add-managed-subdomain/", {
                fqdn: fqdn,
                workspaceId: props.workspaceId,
                next_stage_number: 1,
            }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: (response) => {
            props.setShowLoader(false);
            const nameservers: string[] = response.data["nameservers"];
            const subdomain: string = response.data["subdomain"];
            props.dispatch({
                type: "ADD_MANAGED_SUBDOMAIN",
                subdomain: subdomain,
                nameservers: nameservers,
            });
        },
        onError: (error: ApiRequestFailed) => {
            props.setShowLoader(false);
            console.error(error);
            enqueueSnackbar(error.data.message || "An error occurred", {
                variant: "error",
            });
        }
    });

    // Function to validate domain and TLD using admin context constraints
    const validateDomainAndTLD = async (domainValue: string): Promise<boolean> => {
        setDomainError("");

        if (!domainValue.trim()) {
            setDomainError("Domain cannot be empty");
            return false;
        }

        // Check domain format structure
        if (!isValidDomainFormat(domainValue)) {
            setDomainError("Invalid domain format. Please enter a valid domain name.");
            return false;
        }

        // Extract domain TLD
        const tld = extractDomainTLDFromDomain(domainValue);
        if (!tld) {
            setDomainError("Unable to extract domain TLD from domain. Please check the domain format.");
            return false;
        }

        // Validate domain TLD against allowed list for this workspace context
        setIsValidatingTLD(true);
        try {
            const isTLDAllowed = await validateDomainTLDAllowed(tld);
            if (!isTLDAllowed) {
                setDomainError(`The domain TLD '.${tld}' is not allowed for domain connections. Please use a different domain.`);
                return false;
            }
        } catch (error) {
            console.error("Error validating TLD:", error);
            // Gracefully fall back/continue with flow operations if checking breaks
        } finally {
            setIsValidatingTLD(false);
        }

        return true;
    };

    // Handle domain input changes cleanly with real-time feedback resets
    const handleDomainChange = (value: string) => {
        setDomain(value);
        setDomainError(""); 
    };

    // Handle proceed submission rules
    const handleProceed = async () => {
        const isValid = await validateDomainAndTLD(domain);
        if (isValid) {
            props.setShowLoader(true);
            addManagedSubdomainMutation.mutate(domain);
        }
    };

    return (
        <>
            {/* Heading Content Section */}
            <Container maxWidth="sm">
                <Typography variant="h4" align="center" color="primary">
                    Add your domain
                </Typography>
                <Typography variant="subtitle1" align="center" sx={{ lineHeight: 1.5, mb: 2 }}>
                    We will create sub-domains using this domain.<br/>
                    The created sub-domain will then be used for creating email accounts.
                </Typography>

                {/* Alert Warning Box */}
                <Accordion 
                    defaultExpanded
                    sx={{
                        mt: 4, 
                        backgroundColor: "floralwhite",
                        border: "none",
                        boxShadow: "none",
                        '&.MuiAccordion-root.Mui-expanded': { mt: 4 }
                    }}
                >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                            <WarningAmber color="warning" sx={{ mt: 0.5 }} />
                            {/* Fixed typo and wrapped inside the sx prop */}
                            <Box sx={{ textAlign: "left" }}>
                                <Typography component="span" fontWeight="bold" display="block">
                                    Important:
                                </Typography>
                                <Typography component="ul" sx={{ pl: 2, mt: 0.5, fontSize: "0.875rem", listStyleType: "disc" }}>
                                    <li>Do not use your primary domain for sending cold emails.</li>
                                    <li>We recommend using a <strong>“.com”</strong> domain, as it consistently delivers the best results.</li>
                                </Typography>
                            </Box>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pl: 7 }}>
                        <Typography variant="body2" color="textSecondary">
                            If you already have a G-suite or a website on the domain you're connecting, 
                            updating the NS records will stop them from functioning. 
                            It is advised that you buy/use a domain that doesn't have a mailbox or live website.
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            </Container>

            {/* Input Control Field Section */}
            <TextField 
                id="domain"
                value={domain}
                onChange={e => handleDomainChange(e.target.value)}
                disabled={isValidatingTLD}
                variant="outlined"
                placeholder="example.com"
                error={!!domainError}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !isValidatingTLD && !domainError && domain.trim()) {
                        handleProceed();
                    }
                }}
                sx={{
                    mt: 4,
                    mb: 1,
                    width: { xs: "100%", lg: "350px" }
                }}
            />
            
            {domainError && (
                <Typography 
                    variant="body2" 
                    color="error" 
                    align="center" 
                    sx={{ width: { xs: "100%", lg: "350px" }, mt: 0.5 }}
                >
                    {domainError}
                </Typography>
            )}

            {/* Action Group Footer */}
            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="row" sx={{ mt: 4 }}>
                <CancelButton userId={props.userId} />
                <Button 
                    variant="contained" 
                    sx={{ ml: 1 }} 
                    endIcon={<NavigateNext />}
                    disabled={isValidatingTLD || !!domainError || !domain.trim()}
                    onClick={handleProceed}
                >
                    Proceed
                </Button>
            </Box>
        </>
    );
}

function AddNameservers(props: {
    userId: string;
    nameservers: string[];
    subdomain: string;
    dispatch: Dispatch<Action>;
}) {
    const { enqueueSnackbar } = useSnackbar();
    
    const proceedRef = useRef<HTMLButtonElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Auto-focus container layout wrapper to handle global page event triggers
    useEffect(() => {
        containerRef.current?.focus();
    }, []);

    const handleCopyAll = () => {
        navigator.clipboard.writeText(props.nameservers.join("\n")).then(() => {
            enqueueSnackbar("All Nameservers Copied!", { variant: "success" });
        });
    };

    return (
        <Box
            ref={containerRef}
            tabIndex={0}
            sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                outline: "none"
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    proceedRef.current?.click();
                }
            }}
        >
            {/* Main Center Content Shield */}
            <Box sx={{ maxWidth: 750, width: "100%" }}>
                
                {/* Heading Content Section */}
                <Typography variant="h4" align="center" color="primary" sx={{ mb: 1, fontWeight: "medium" }}>
                    Point <Box component="span" sx={{ px: 1, bgcolor: "action.selected", borderRadius: 0.5 }}>{props.subdomain}</Box> domain to our Nameservers
                </Typography>
                
                <Typography variant="body1" align="center" color="textSecondary" sx={{ mt: 2, px: 2, lineHeight: 1.6 }}>
                    Copy the NS (Nameserver) records from here & update them in your domain provider’s DNS settings. Once done, we'll auto-create your Cold Email Infrastructure.
                </Typography>              

                {/* Data Records Table Layout */}
                <Box sx={{ mt: 4 }}>
                    <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto", border: "1px solid", borderColor: "divider", boxShadow: "none" }}>
                        <Table aria-label="nameservers table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell align="left">Name</TableCell>
                                    <TableCell align="left">Nameserver</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {props.nameservers.map((ns, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell component="th" scope="row">NS</TableCell>
                                        <TableCell align="left">
                                            {props.subdomain}&nbsp;<CopyButton text={props.subdomain}/>
                                        </TableCell>
                                        <TableCell align="left">
                                            {ns}&nbsp;<CopyButton text={ns}/>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Table Actions Group */}
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 3 }}>
                        <Button 
                            variant="outlined" 
                            startIcon={<IconCopy />}
                            onClick={handleCopyAll}
                        >
                            Copy All Nameservers
                        </Button>
                    </Box>
                </Box>

                {/* Instructional Guides & Support Links */}
                <Box sx={{ mt: 4 }}>
                    <Typography variant="body1" align="center">
                        How to add NS records on:&nbsp;
                        <Link href="https://deliveryman.ai/blog/how-to-add-ns-records-in-namecheap" target="_blank" rel="noreferrer" underline="hover">Namecheap</Link> |&nbsp;
                        <Link href="https://deliveryman.ai/blog/how-to-add-ns-records-in-godaddy" target="_blank" rel="noreferrer" underline="hover">Godaddy</Link> |&nbsp;
                        <Link href="https://deliveryman.ai/blog/how-to-add-ns-records-in-porkbun" target="_blank" rel="noreferrer" underline="hover">Porkbun</Link> |&nbsp;
                        <Link href="https://deliveryman.ai/blog/how-to-add-ns-records-in-hostinger" target="_blank" rel="noreferrer" underline="hover">Hostinger</Link> |&nbsp;
                        <Link href="https://deliveryman.ai/blog/?category=DNS%20Setup" target="_blank" rel="noreferrer" underline="hover">Others</Link>
                    </Typography>
                    
                    <Typography variant="body1" align="center" sx={{ mt: 3 }}>
                        If you are having trouble, please get in touch with us on our&nbsp;
                        <Box component="span" sx={{ textDecoration: "underline", cursor: "pointer", fontWeight: "medium" }}>
                            Live Chat Support
                        </Box>
                    </Typography>
                </Box>

                {/* Flow Navigation Footer */}
                <Box display="flex" justifyContent="center" alignItems="center" flexDirection="row" sx={{ mt: 4 }}>
                    <CancelButton userId={props.userId} />
                    <Button 
                        ref={proceedRef}
                        variant="contained" 
                        sx={{ ml: 1 }} 
                        endIcon={<NavigateNext />}
                        onClick={() => {
                            props.dispatch({
                                type: "NAMESERVERS_ADDED"
                            });
                        }}
                    >
                        I have added the records
                    </Button>
                </Box>

            </Box>
        </Box>
    );
}

function CheckNsRecords(props: {
    userId: string,
    workspaceId: string,
    subdomain: string,
    dispatch: Dispatch<Action>
}) {
    // Query for checking NS records
    const checkNsRecordsQuery = useQuery({
        queryKey: ["checkNsRecords"],
        queryFn: () => authenticateAndFetchData(`/admin/connect/check-ns-records/?subdomain=${props.subdomain}&workspaceId=${props.workspaceId}`),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
        refetchInterval: 10000,
    });
    useEffect(() => {
        if (checkNsRecordsQuery.data) {
            console.log(checkNsRecordsQuery.data.data["success"])
            if (checkNsRecordsQuery.data.data["success"]) {
                props.dispatch({
                    type: 'NAMESERVERS_VERIFIED'
                });
            }
        }
    }, [checkNsRecordsQuery.data, props]);

    return (
        <Stack direction={"column"} spacing={4} justifyContent={"center"} alignItems={"center"}>
            <Container maxWidth={"md"}>
                <Typography variant={"h4"} align={"center"} color={"primary"}>
                    Verifying nameserver records for <b>{props.subdomain}</b>...
                </Typography>
                <Typography variant={"subtitle1"} align={"center"} sx={{mt: 2}}>
                    It might take some time for the nameserver changes to propagate. Ideally this should only take
                    between 5-10 mins or - in very rare cases - upto an hour.
                    You can safely cancel and retry later if required.<br/><br/>
                    In case you are stuck on this step, please
                    get in touch with us on
                    {/* eslint-disable-next-line no-script-url,jsx-a11y/anchor-is-valid */}
                    our <a href={"javascript:void(Tawk_API.toggle())"} style={{whiteSpace: "nowrap"}}>Live Support Chat</a> for
                    assistance.
                </Typography>
            </Container>
            <l-mirage size="120" color={"#42a5f5"}></l-mirage>
            {/* Actions */}
            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection={"row"} sx={{mt: 4}}>
                <CancelButton userId={props.userId!}/>
            </Box>
        </Stack>
    )
}

type NameStrategies = "random" | "male" | "female" | "custom"

function EmailAndSubdomainGeneration(props: {
    userId: string;
    workspaceId: string;
    subdomain: string;
    dispatch: Dispatch<Action>;
    setShowLoader: (value: boolean) => void;
}) {
    const { enqueueSnackbar } = useSnackbar();

    const [nameStrategy, setNameStrategy] = useState<NameStrategies>("random");
    const [customNames, setCustomNames] = useState<CustomNameRow[]>([{ first: "", last: "" }]);
    const [countryName, setCountryName] = useState<string>("");
    
    // Track row-by-row input errors locally to match user validation strictness
    const [errors, setErrors] = useState<CustomNameRow[]>([{ first: "", last: "" }]);

    const handleNameChange = (index: number, field: "first" | "last", value: string) => {
        const updatedNames = customNames.map((n, i) =>
            i === index ? { ...n, [field]: value } : n
        );
        setCustomNames(updatedNames);
        validateFields(updatedNames);
    };

    const validateFields = (names: CustomNameRow[]) => {
        const newErrors = names.map((n) => {
            const err = { first: "", last: "" };
            const first = n.first.trim().toLowerCase();
            const last = n.last.trim().toLowerCase();

            // Validation 1: Minimum 3 characters
            if (first && first.length < 3) err.first = "Min 3 characters required.";
            if (last && last.length < 3) err.last = "Min 3 characters required.";

            // Validation 2: First 2 characters of first and last name cannot be the same
            if (
                first.length >= 2 &&
                last.length >= 2 &&
                first.slice(0, 2) === last.slice(0, 2)
            ) {
                err.last = "First 2 characters of first and last name cannot be the same.";
            }

            return err;
        });
        setErrors(newErrors);
    };

    const handleAdd = () => {
        setCustomNames((prev) => [...prev, { first: "", last: "" }]);
        setErrors((prev) => [...prev, { first: "", last: "" }]);
    };

    const handleRemove = (index: number) => {
        setCustomNames((prev) => prev.filter((_, i) => i !== index));
        setErrors((prev) => prev.filter((_, i) => i !== index));
    };

    const hasErrors = errors.some((e) => e.first || e.last);

    const isCustomInvalid =
        nameStrategy === "custom" &&
        (customNames.length === 0 || customNames.some((n) => !n.first.trim() || !n.last.trim()));

    // Mutation for saving backend configuration metrics
    const saveDataMutation = useMutation({
        mutationKey: ["saveStrategyAndContactCount"],
        mutationFn: () =>
            authenticateAndPostData("/admin/connect/save-strategy-contact-count/", {
                workspaceId: props.workspaceId,
                subdomain: props.subdomain,
                naming_strategy: nameStrategy,
                custom_name: nameStrategy === "custom" ? customNames.map((n) => `${n.first} ${n.last}`).join(";") : null,
                contacts_count: 2000,
                next_stage_number: 4,
                country: countryName,
            }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            props.setShowLoader(false);
            // Move onto polling page.
            props.dispatch({
                type: "NAMING_STRATEGY_SET",
                namingStrategy: nameStrategy,
                customName: customNames.map((n) => `${n.first} ${n.last}`),
                contactsCount: 2000,
            });
        },
        onError: (error: ApiRequestFailed) => {
            props.setShowLoader(false);
            console.error(error);
            enqueueSnackbar(error.data.message || "An unexpected error occurred", {
                variant: "error",
            });
        },
    });

    const handleProceed = () => {
        if (!nameStrategy) {
            enqueueSnackbar("Please set valid values for naming strategy.", { variant: "error" });
            return;
        }

        if (isCustomInvalid) {
            enqueueSnackbar("Please provide valid firstname and lastname for all custom naming entries.", { variant: "error" });
            return;
        }

        if (nameStrategy !== "custom" && !countryName) {
            enqueueSnackbar("Please select a country.", { variant: "error" });
            return;
        }

        props.setShowLoader(true);
        saveDataMutation.mutate();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const isDisabled =
                saveDataMutation.isPending ||
                hasErrors ||
                isCustomInvalid ||
                (nameStrategy !== "custom" && !countryName);

            if (!isDisabled) {
                handleProceed();
            }
        }
    };

    return (
        <Stack
            direction="column"
            spacing={4}
            justifyContent="center"
            alignItems="center"
            onKeyDown={handleKeyDown}
            sx={{ width: "100%" }}
        >
            {/* Heading Content View Block */}
            <Container maxWidth="sm">
                <Typography variant="h4" align="center" color="primary" sx={{ fontWeight: "medium" }}>
                    Select Name for Email Sending Accounts for {props.subdomain}
                </Typography>
                <Typography variant="subtitle1" align="center" color="textSecondary" sx={{ mt: 1.5 }}>
                    Choose how to generate sender names for your email accounts. We will automatically create various email
                    combinations based on your selection.
                </Typography>
            </Container>

            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
                {/* Name type selection */}
                <FormControl component="fieldset">
                    <RadioGroup
                        value={nameStrategy}
                        onChange={(e) => setNameStrategy(e.target.value as NameStrategies)}
                    >
                        <FormControlLabel value="random" control={<Radio />} label="Use Random Names" />
                        <FormControlLabel value="female" control={<Radio />} label="Use Any Female Name" />
                        <FormControlLabel value="male" control={<Radio />} label="Use Any Male Name" />
                        <FormControlLabel value="custom" control={<Radio />} label="Use Custom Name" />
                    </RadioGroup>
                </FormControl>

                {/* Custom Name Generation Fields mapping */}
                {nameStrategy === "custom" && (
                    <Stack direction="column" spacing={2.5} sx={{ mt: 3, width: "100%", maxWidth: 600 }}>
                        {customNames.map((name, index) => (
                            <Stack direction="row" spacing={2} key={index} alignItems="flex-start" justifyContent="center">
                                <TextField
                                    type="text"
                                    label="First Name"
                                    placeholder="Enter first name"
                                    value={name.first}
                                    onChange={(e) => handleNameChange(index, "first", e.target.value)}
                                    error={!!errors[index]?.first}
                                    helperText={errors[index]?.first}
                                    sx={{ width: 220 }}
                                    required
                                />
                                <TextField
                                    type="text"
                                    label="Last Name"
                                    placeholder="Enter last name"
                                    value={name.last}
                                    onChange={(e) => handleNameChange(index, "last", e.target.value)}
                                    error={!!errors[index]?.last}
                                    helperText={errors[index]?.last}
                                    sx={{ width: 220 }}
                                    required
                                />
                                {customNames.length > 1 && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleRemove(index)}
                                        sx={{ height: 56 }}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </Stack>
                        ))}
                        
                        {customNames.length < 4 && (
                            <Box display="flex" justifyContent="flex-end" sx={{ width: "100%", pr: customNames.length > 1 ? 13 : 0 }}>
                                <Button variant="outlined" onClick={handleAdd}>
                                    Add More
                                </Button>
                            </Box>
                        )}
                    </Stack>
                )}

                {/* Country Selector Component */}
                {nameStrategy !== "custom" && (
                    <Stack direction="column" spacing={1} sx={{ mt: 3, width: 440 }}>
                        <Typography fontWeight="bold">Select A Country:</Typography>
                        <CountrySelect onCountrySelected={setCountryName} />
                    </Stack>
                )}
            </Box>

            {/* Actions Footer Container */}
            <Box display="flex" justifyContent="center" alignItems="center" flexDirection="row" sx={{ mt: 2 }}>
                <CancelButton userId={props.userId} disable={saveDataMutation.isPending} />
                <Button
                    variant="contained"
                    sx={{ ml: 1 }}
                    endIcon={<NavigateNext />}
                    disabled={
                        saveDataMutation.isPending ||
                        hasErrors ||
                        isCustomInvalid ||
                        (nameStrategy !== "custom" && !countryName)
                    }
                    onClick={handleProceed}
                >
                    Proceed
                </Button>
            </Box>
        </Stack>
    );
}

function EmailGenerationPollingPage(props: {
    workspaceId: string,
    subdomain: string,
    dispatch: Dispatch<Action>
}) {
    // This will poll the given URL every 10 seconds. The URL should return "success" true or false. Polling will run
    // as long as "success" is false.
    const pollingQuery = useQuery({
        queryKey: ["emailGenerationPolling"],
        queryFn: () => authenticateAndFetchData(`/admin/connect/check-email-generation-done/?subdomain=${props.subdomain}&workspaceId=${props.workspaceId}`),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
        refetchInterval: 10000,
        enabled: !!props.workspaceId && !!props.subdomain
    });
    useEffect(() => {
        if (pollingQuery.data) {
            if (pollingQuery.data.data["success"]) {
                props.dispatch({
                    type: "EMAIL_GENERATION_POLLING",
                });
            }
        }
    }, [pollingQuery.data, props]);

    return (
        <Stack direction={"column"} spacing={4} justifyContent={"center"} alignItems={"center"}>
            <Container maxWidth={"md"}>
                <Typography variant={"h4"} align={"center"} color={"primary"}>
                    Waiting for email id previews to be generated...
                </Typography>
                <Typography variant={"subtitle1"} align={"center"} sx={{mt: 2}}>
                    This might take some time. In case you are stuck on this step, please
                    get in touch with us on
                    {/* eslint-disable-next-line no-script-url,jsx-a11y/anchor-is-valid */}
                    our <a href={"javascript:void(Tawk_API.toggle())"} style={{whiteSpace: "nowrap"}}>Live Support Chat</a> for
                    assistance.
                </Typography>
            </Container>
            <l-mirage size="120" color={"#42a5f5"}></l-mirage>
            {/*/!* Actions *!/*/}
            {/*<Box display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection={"row"} sx={{mt: 4}}>*/}
            {/*	<CancelButton/>*/}
            {/*</Box>*/}
        </Stack>
    )
}

interface EmailPreviewData {
    id: number
    email: string
    subdomain: string
}

function EmailList(props: {
    userId: string,
    workspaceId: string,
    subdomain: string,
    dispatch: Dispatch<Action>,
    setShowLoader: (value: boolean) => void,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [data, setData] = useState<EmailPreviewData[]>([]);
    const [originalData, setOriginalData] = useState<EmailPreviewData[]>([]);
    
    // Track active cell editing status locally if needed to prevent premature submission
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Fetch the email data.
    const fetchPreviewQuery = useQuery({
        queryKey: ["fetchPreviewEmails", props.subdomain, props.workspaceId],
        queryFn: () =>        
            authenticateAndFetchData(`/admin/connect/get-email-preview/?subdomain=${props.subdomain}&workspaceId=${props.workspaceId}`),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,    
        enabled: !!props.workspaceId && !!props.subdomain,
    });

    useEffect(() => {
        if (fetchPreviewQuery.data) {
            const previewData = fetchPreviewQuery.data.data["preview"] as EmailPreviewData[];
            setData(previewData);
            setOriginalData(JSON.parse(JSON.stringify(previewData))); // Deep copy to track original values
        }
    }, [fetchPreviewQuery.data]);

    // Mutation to set up email aliases.
    const setupEmailAliasesMutation = useMutation({
        mutationKey: ["setupEmailAliases"],
        mutationFn: () => {           
            const emailChanges: Record<string, string> = {};
            data.forEach((row, index) => {
                const originalRow = originalData[index];
                if (originalRow && row.email !== originalRow.email) {
                    emailChanges[originalRow.email] = row.email;
                }
            });

            // Admin Side: Standard body payload configuration without passing workspace headers
            return authenticateAndPostData("/admin/connect/setup-email-aliases/", {
                workspaceId: props.workspaceId,
                subdomain: props.subdomain,
                email_changes: emailChanges,
                next_stage_number: 5,
            });
        },
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            props.setShowLoader(false);
            props.dispatch({
                type: "EMAIL_ALIASES_CREATED",
            });
        },
        onError: (error: ApiRequestFailed) => {
            props.setShowLoader(false);
            enqueueSnackbar(error?.data?.message || "Something went wrong", {
                variant: "error",
            });
            console.error(error?.data?.message);
        }
    });

    const columns: GridColDef[] = [
        {
            field: 'email',
            headerName: 'Email ID',
            flex: 1,
            editable: true,
            renderCell: (params) => (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    sx={{
                        '&:hover .edit-icon': {
                            opacity: 1,
                        },
                    }}
                >
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {params.value}
                    </Typography>
                    <Edit
                        className="edit-icon"
                        sx={{
                            fontSize: 16,
                            color: 'text.secondary',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            ml: 1,
                        }}
                    />
                </Box>
            ),
        },
        {
            field: 'subdomain',
            headerName: 'Subdomain',
            flex: 1,
        },
    ];



    const paginationModel = { page: 0, pageSize: 10 };

    return (
        <Box display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}
            sx={{ width: "100%", pb: 6 }}>
            {/* Heading */}
            <Stack direction={"column"}>
                <Typography variant="h4" align={"center"} color={"primary"}>
                    Email IDs That Will Be Generated.
                </Typography>
                <Typography variant="subtitle1" align={"center"}>
                    We will be using these emails to send out emails to your contacts.<br />
                    We have generated {data.length} emails for you. There will be no charge for any of this.
                </Typography>
            </Stack>

            <Box sx={{ width: { sm: "90%" }, mt: 4 }}>
                <DataGrid
                    rows={data}
                    columns={columns}
                    editMode="row"
                    loading={fetchPreviewQuery.isLoading}
                    pageSizeOptions={[10, 50, 100]}
                    initialState={{ pagination: { paginationModel } }}
                    onRowEditStart={() => setIsEditing(true)}
                    onRowEditStop={() => setIsEditing(false)}
                    processRowUpdate={(newRow, oldRow) => {
                        if (!newRow.email || !newRow.email.trim()) {
                            enqueueSnackbar("Email cannot be empty.", { variant: "warning" });
                            return oldRow;
                        }

                        const isDuplicate = data.some(row => 
                            row.email.toLowerCase() === newRow.email.toLowerCase() && row.id !== newRow.id
                        );
                        if (isDuplicate) {
                            enqueueSnackbar("This email already exists. Please choose a different name.", { variant: "error" });
                            return oldRow;
                        }

                        setData(prev => prev.map(row => row.id === newRow.id ? newRow : row));
                        return newRow;
                    }}
                    slotProps={{
                        loadingOverlay: {
                            variant: 'linear-progress', // Properly typed for MUI DataGrid
                        },
                    }}
                />
            </Box>

            {/* Actions */}
            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection={"row"} sx={{ mt: 4 }}>
                <CancelButton userId={props.userId} disable={setupEmailAliasesMutation.isPending}/>
                <Button 
                    variant={"contained"}
                    sx={{ ml: 1 }}
                    endIcon={<NavigateNext />}
                    // Disabled if a request is active OR the admin hasn't finished typing out their updates in the active row
                    disabled={setupEmailAliasesMutation.isPending || isEditing}
                    onClick={() => {
                        props.setShowLoader(true);
                        setupEmailAliasesMutation.mutate();
                    }}>
                    Finish
                </Button>
            </Box>
        </Box>
    )
}

function EmailSetupPollingPage(props: {
    workspaceId: string,
    subdomain: string,
    dispatch: Dispatch<Action>
}) {
    // This will poll the given URL every 10 seconds. The URL should return "success" true or false. Polling will run
    // as long as "success" is false.
    const pollingQuery = useQuery({
        queryKey: ["emailSetupPolling"],
        queryFn: () => authenticateAndFetchData(`/admin/connect/check-email-setup-done/?subdomain=${props.subdomain}&workspaceId=${props.workspaceId}`),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
        refetchInterval: 10000,
    });
    useEffect(() => {
        if (pollingQuery.data) {
            console.log(pollingQuery.data.data["success"]);
            if (pollingQuery.data.data["success"]) {
                props.dispatch({
                    type: "EMAIL_SETUP_POLLING",
                });
            }
        }
    }, [pollingQuery.data, props]);

    return (
        <Stack direction={"column"} spacing={4} justifyContent={"center"} alignItems={"center"}>
            <Container maxWidth={"md"}>
                <Typography variant={"h4"} align={"center"} color={"primary"}>
                    Waiting for Email ID setup to complete...
                </Typography>
                <Typography variant={"subtitle1"} align={"center"} sx={{mt: 2}}>
                    This might take some time. In case you are stuck on this step, please
                    get in touch with us on
                    {/* eslint-disable-next-line no-script-url,jsx-a11y/anchor-is-valid */}
                    our <a href={"javascript:void(Tawk_API.toggle())"} style={{whiteSpace: "nowrap"}}>Live Support Chat</a> for
                    assistance.
                </Typography>
            </Container>
            <l-mirage size="120" color={"#42a5f5"}></l-mirage>
            {/*/!* Actions *!/*/}
            {/*<Box display={"flex"} justifyContent={"center"} alignItems={"center"} flexDirection={"row"} sx={{mt: 4}}>*/}
            {/*	<CancelButton/>*/}
            {/*</Box>*/}
        </Stack>
    )
}

interface SetupCompleteData {
    status_code: string;
    status_text: string;
    domain: string;
    total_emails: number;
    domain_usable_from_ts: number;
}

interface Step {
    title: string;
    description: string;
}

// Generates structural step updates matching the state definitions of user-side views
const getSteps = (data: SetupCompleteData, domainStatus: string): Step[] => [
    {
        title: "NS Records Propagated Successfully",
        description: "We have set up your SPF, DKIM, & DMARC.",
    },
    {
        title: "Domain Connected Successfully",
        description: `Your domain ${data.domain} is now connected to our servers.`,
    },
    {
        title: "Subdomains Created Successfully",
        description: "All email subdomains have been created and configured.",
    },
    {
        title: "Email Accounts Created Successfully",
        description: `We have created ${data.total_emails} email accounts for you.`,
    },
    {
        title: domainStatus === "warmup" ?
            "Cold Email Infrastructure Setup Completed Successfully"
            : "Cold Email Infrastructure Setup",
        description: domainStatus === "warmup" ?
            "We have set up an infrastructure designed to meet your expectations."
            : "We are setting up an infrastructure designed to meet your expectations.",
    },
];

function SetupComplete(props: {
    userId: string;
    workspaceId: string;
    subdomain: string;
}) {
    const navigate = useNavigate();
    const [setupCompleteData, setSetupCompleteData] = useState<SetupCompleteData>();
    const [domainStatus, setDomainStatus] = useState<string>("");
        
    const [stepIndex, setStepIndex] = useState(0);
    const [phase, setPhase] = useState<"icon" | "line">("icon");
    const [refetchCount, setRefetchCount] = useState(0);
    const [startPolling, setStartPolling] = useState(false);

    const isCompleted = domainStatus === "warmup";
    const isError = domainStatus === "error" || domainStatus === "postal_error";
    const isPolling = startPolling && domainStatus === "postal_setup" && !isError && !isCompleted && refetchCount < 8;

    const emailAccountsQuery = useQuery({
        queryKey: ["adminSuccessEmailAccounts", props.subdomain],
        queryFn: () => authenticateAndFetchData(`/admin/email-accounts/?workspace_id=${props.workspaceId}`),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
        refetchInterval: isPolling ? 25000 : false,
        refetchIntervalInBackground: true,
        enabled: !!props.workspaceId
    });

    useEffect(() => {
        if (emailAccountsQuery.data) {
            const matchedEmailAccount = emailAccountsQuery.data?.data?.managed_subdomains?.find(
                (item: any) => item.managed_subdomain === props.subdomain
            );
            const status = matchedEmailAccount?.status || "";
            setDomainStatus(status);

            if (startPolling && status === "postal_setup") {
                setRefetchCount((prev) => prev + 1);
            }
        }
    }, [emailAccountsQuery.data, startPolling]);

    
    useEffect(() => {
        if (domainStatus === "postal_setup") {
            const timer = setTimeout(() => {
                setStartPolling(true);
            }, 45000);
            return () => clearTimeout(timer);
        }
    }, [domainStatus]);
    
    const setupCompleteQuery = useQuery({
        queryKey: ["adminSetupCompleteQuery", props.subdomain],
        queryFn: () => authenticateAndFetchData(
            `/admin/connect/success-stage-data/?domain=${props.subdomain}&workspaceId=${props.workspaceId}`,
            "json"
        ),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
        enabled: !!props.workspaceId,
    });

    useEffect(() => {
        if (setupCompleteQuery.data) {
            setSetupCompleteData(setupCompleteQuery.data.data);
        }
    }, [setupCompleteQuery.data]);

    const steps = setupCompleteData ? getSteps(setupCompleteData, domainStatus) : [];
    
    useEffect(() => {
        if (!steps.length) return;
        if (stepIndex >= steps.length) return;

        const timer = setTimeout(() => {
            if (phase === "icon") {
                setPhase("line");
            } else {
                setPhase("icon");
                setStepIndex((prev) => prev + 1);
            }
        }, phase === "icon" ? 1000 : 1200);

        return () => clearTimeout(timer);
    }, [stepIndex, phase, steps.length]);

    if (setupCompleteQuery.isLoading) {
        return <PageLoading />;
    } 
    
    if (setupCompleteQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={setupCompleteQuery.error as unknown as ApiRequestFailed} />;
    }

    if (setupCompleteData) {
        return (
            <Stack direction="column" spacing={4} justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
                {/* Back Navigation Bar Layout */}
                <Box width="100%" display="flex" justifyContent="flex-start">
                    <IconButton onClick={() => navigate(urls["adminUserDetails"].replace(":userId", props.userId || ""))}>
                        <ArrowBack />
                    </IconButton>
                </Box>
       
                <Paper 
                    elevation={2}
                    sx={{
                        p: 6,
                        width: "100%",
                        maxWidth: "888px"
                    }}
                >
                    <Stack direction="column" spacing={4}>                                        
                        <Typography variant="h4" fontWeight="bold" textAlign="center">
                            Cold Email Infrastructure for <br />
                            <Box component="span" sx={{ px: 1, py: 0.5, bgcolor: 'action.selected', borderRadius: 1, mx: 1, fontSize: 'inherit', fontFamily: 'monospace' }}>
                                {props.subdomain}
                            </Box> 
                            {isCompleted ? <span style={{ color: '#2e7d32' }}>Completed</span> : "being built."}
                        </Typography>        

                        {!isCompleted && (
                            <Typography variant="body1" color="text.secondary" textAlign="center">
                                This takes a few minutes to complete.<br/>
                                Once done we'll auto start building your domain reputation.<br/>
                                <span style={{ color: '#1976d2', fontSize: '14px' }}>You don't need to stay on this page. The setup will finish in the background.</span>
                            </Typography>
                        )}
                        
                        <Stack direction="column" spacing={0} sx={{ pl: 4, mt: 2 }}>
                            {steps.map((step, index) => {
                                const isStepCompleted = index < stepIndex;
                                const isStepActive = index === stepIndex;

                                const iconState = isStepCompleted ? "done" : isStepActive ? (phase === "icon" ? "running" : "done") : "pending";
                                const lineState = isStepCompleted ? "done" : isStepActive ? (phase === "line" ? "running" : "pending") : "pending";
                                const isLast = index === steps.length - 1;

                                return (
                                    <AdminStepItem
                                        key={step.title}
                                        step={step}
                                        iconState={iconState}
                                        lineState={lineState}
                                        isLast={isLast}
                                        domainStatus={domainStatus}
                                    />
                                );
                            })}
                        </Stack>

                        {isCompleted && (
                            <Stack spacing={1} sx={{ mt: 2, pl: 4 }}>
                                <Typography variant="body1">We have auto-started building domain reputation & email warmup.</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    You’ll be able to launch a campaign after your domain completes the 14-day reputation-building period.
                                </Typography>
                            </Stack>
                        )}

                        <hr style={{ border: 'none', borderTop: '1px solid rgba(0,0,0,0.12)', margin: '16px 0' }} />

                        {/* Warmup Timelines and Functional Date Layout Elements */}
                        <Stack spacing={2} direction="row" justifyContent="center" flexWrap="wrap" useFlexGap gap={2}>
                            {/* <Button 
                                variant="contained" 
                                color="inherit"
                                onClick={() => {
                                    window.location.replace(getLoggedInUrl("vConnectNewDomain", props.workspaceId || ""));
                                }}
                            >
                                Add Another Domain
                            </Button> */}
                            <Button 
                                variant="contained"
                                onClick={() => {
                                    navigate(urls["adminUserConnectedDomains"].replace(":userId", props.userId || ""));
                                }}
                                endIcon={<ArrowRight />}
                            >
                                View User Connected Domains
                            </Button>
                        </Stack>

                        {setupCompleteData.domain_usable_from_ts && (
                            <Box textAlign="center" sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Domain usable from: {format(new Date(setupCompleteData.domain_usable_from_ts), "do MMM y, HH:mm:ss (xxx)")}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Paper>
            </Stack>
        );
    }

    return null;
}


function AdminStepItem({
    step,
    iconState,
    lineState,
    isLast,
    domainStatus
}: {
    step: Step;
    iconState: "done" | "running" | "pending";
    lineState: "done" | "running" | "pending";
    isLast: boolean;
    domainStatus: string;
}) {
    return (
        <Box display="flex" gap={2.5}>
            {/* LEFT PIPELINE TRACK */}
            <Box display="flex" flexDirection="column" alignItems="center" position="relative" sx={{ mt: 0.5 }}>
                {/* Gray Base connector line */}
                {!isLast && (
                    <Box sx={{ position: 'absolute', top: 20, left: 10, bottom: 0, width: '1px', bgcolor: 'divider', zIndex: 1 }} />
                )}

                {/* CSS Animated progress connector line */}
                {!isLast && lineState !== "pending" && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 20,
                            left: 10,
                            width: lineState === "done" ? '1.5px' : '1px',
                            backgroundColor: lineState === "done" ? 'text.primary' : '#1976d2',
                            zIndex: 2,
                            height: '100%',
                            transformOrigin: 'top',
                            animation: lineState === "running" ? 'growLine 1.2s ease-in-out forwards' : 'none'
                        }}
                    />
                )}

                {/* Status Indicator Bubble */}
                <Box sx={{ zIndex: 3, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20 }}>
                    {/* CSS Keyframes Injection for UI Loader effects */}
                    <style>{`
                        @keyframes growLine { from { transform: scaleY(0); } to { transform: scaleY(1); } }
                        @keyframes shimmer { from { background-position-x: 100%; } to { background-position-x: -100%; } }
                        @keyframes pulseScale { 0% { transform: scale(0.9); opacity: 0.6; } 50% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(0.9); opacity: 0.6; } }
                        @keyframes cssSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                    `}</style>

                    {iconState === "done" && (
                        isLast && domainStatus !== "warmup" ? (
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', animation: 'pulseScale 1.8s infinite' }} />
                        ) : (
                            <TaskAlt sx={{ width: 20, height: 20, color: '#2e7d32' }} />
                        )
                    )}

                    {iconState === "running" && (
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #1976d2', borderTopColor: 'transparent', animation: 'cssSpin 1s linear infinite' }} />
                    )}

                    {iconState === "pending" && (
                        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '1px solid', borderColor: 'text.disabled' }} />
                    )}
                </Box>
            </Box>

            {/* TEXT CONTAINER */}
            <Box display="flex" flexDirection="column" sx={{ pb: 3.5 }}>
                <Typography variant="body1" fontWeight="600">
                    {iconState === "running" || (iconState === "done" && isLast && domainStatus !== "warmup") ? (
                        <Box
                            component="span"
                            sx={{
                                backgroundImage: 'linear-gradient(110deg, #020617, 45%, #71717a, 55%, #020617)',
                                backgroundSize: '200% 100%',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                display: 'inline-block',
                                animation: 'shimmer 2.2s linear infinite'
                            }}
                        >
                            {step.title}
                        </Box>
                    ) : (
                        step.title
                    )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {iconState === "done" ? step.description : iconState === "running" ? "Running..." : "Waiting to start..."}
                </Typography>
            </Box>
        </Box>
    );
}